import { RequestStatusRenderer } from "components/requestStatsRenderer/RequestStatusRenderer";
import { FeatureFlags } from "featureFlags";
import { GenericPerformanceList } from "features/Reporting/Content/shared/GenericPerformanceList";
import { useFeatureFlag } from "hooks/useFeatureFlag";
import { type FC, useEffect } from "react";
import { type ConnectedProps, connect } from "react-redux";
import { bindActionCreators } from "redux";
import { Icon, type SemanticICONS } from "semantic-ui-react";
import { type ColumnOption } from "../../../../interfaces/columnOptions";
import { type AppDispatch, type RootState } from "../../../Application/globaltypes/redux";
import {
  type EngagementLineStatus,
  type EngagementPeopleData,
  selectEngagementPeopleData,
  selectEngagementPeopleValue,
} from "../state/slices/videoPerformanceSlice";
import * as videoPerformanceActions from "../state/thunks/videoPerformanceThunk";
import { TextTruncate } from "components";
import { Tooltip } from "components/common/tooltip";
import {
  type PerformanceWithAccountFilter,
  canUseChildAccount,
  completes,
  formatUserName,
  isUserDeleted,
  starts,
} from "features/Library/Common/utils/performanceUtils";
import { bindAction } from "interfaces";
import { setHasDataToExport } from "features/Reporting/state/export/exportSlice";
import { RStatus } from "features/Application/globaltypes/fetchRequest";
import { RouteNames } from "enums";

import styles from "./VideoPerformancePeople.module.scss";
import { MoboLink } from "components/MoboLink/MoboLink";
import { VideoPerformancePeopleStatusToCell } from "./constants";

export type VideoPerformancePeopleProps = {
  videoId: number;
  flowId?: number;
  filter: PerformanceWithAccountFilter;
  includeAccounts?: boolean;
  childAccountId?: number;
  hasCreateAccountsAddOn?: boolean;
} & PropsFromRedux;

type VideoEntity = EngagementPeopleData[number];

export type EmailStatusColors = "icon-send-complete" | "icon-send-progress" | "icon-send-canceled";

export type VideoStatusCompute = {
  [key in EngagementLineStatus]: {
    icon: SemanticICONS;
    color: EmailStatusColors;
  };
};
const getColumnOptions = (
  reportEnabled: boolean,
  includeAccounts?: boolean,
  childAccountId?: number,
  hasCreateAccountsAddOn?: boolean,
) => {
  const baseColumnOptions: ColumnOption<VideoEntity>[] = [
    {
      name: "NAME",
      width: 4,
      isSortable: false,
      render: (entity: VideoEntity) => {
        const formattedNameDisplay = formatUserName(entity.UserFirstName, entity.UserLastName);
        const childAccountEnabled = canUseChildAccount(childAccountId, hasCreateAccountsAddOn);
        const shouldShowLink =
          !isUserDeleted(entity.UserFirstName, entity.UserLastName) && (!includeAccounts || childAccountEnabled);
        const secondaryInfo = [entity.UserJobTitle, entity.UserDepartment].filter(Boolean).join(", ");
        const userName = (
          <Tooltip target={<TextTruncate>{formattedNameDisplay}</TextTruncate>} content={formattedNameDisplay} />
        );
        return (
          <div>
            {shouldShowLink ? (
              <MoboLink
                to={`/${RouteNames.peopleUsers}/${entity.UserId}`}
                className={styles.linkButton}
                accountId={childAccountEnabled ? childAccountId : undefined}
                target={childAccountEnabled ? "_blank" : undefined}
              >
                {userName}
              </MoboLink>
            ) : (
              userName
            )}
            {secondaryInfo && (
              <Tooltip
                target={<TextTruncate className="workTitle">{secondaryInfo}</TextTruncate>}
                content={secondaryInfo}
              />
            )}
          </div>
        );
      },
    },
    {
      name: "EMAIL",
      width: 4,
      isSortable: false,
      render: (entity: VideoEntity) => {
        return <Tooltip target={<TextTruncate>{entity.UserEmail}</TextTruncate>} content={entity.UserEmail} />;
      },
    },
    {
      name: "DATE WATCHED",
      width: 3,
      isSortable: false,
      render: (entity: VideoEntity) => {
        const UTCDate = entity.DateWatched?.match(/\d{4}-\d{2}-\d{2}/);
        let formattedDate: string;
        if (UTCDate) {
          const splitUTCDate = UTCDate[0].split("-");

          const year = splitUTCDate[0];
          const month = splitUTCDate[1];
          const day = splitUTCDate[2];

          formattedDate = `${month}/${day}/${year}`;
        } else {
          formattedDate = "-";
        }

        return (
          <div>
            <div>{formattedDate}</div>
            <div>&nbsp;</div>
          </div>
        );
      },
    },
    {
      name: "STATUS",
      width: 3,
      isSortable: false,
      render: (entity: VideoEntity) => (
        <div className={styles.status}>
          <div>
            <Icon
              name={VideoPerformancePeopleStatusToCell[entity.Status].icon}
              className={VideoPerformancePeopleStatusToCell[entity.Status].color}
            />
            &nbsp;
            {entity.Status}
          </div>
          <div>&nbsp;</div>
        </div>
      ),
    },
  ];

  if (reportEnabled) {
    baseColumnOptions.splice(
      2,
      0,
      {
        name: `Total ${starts}`,
        width: 3,
        isSortable: false,
        render: (entity) => (
          <div>
            <div>{(entity.VideoStarts?.toLocaleString() || entity.videoStarts?.toLocaleString()) ?? 0}</div>
            <div>&nbsp;</div>
          </div>
        ),
      },
      {
        name: `Total ${completes}`,
        width: 3,
        isSortable: false,
        render: (entity: VideoEntity) => (
          <div>
            <div>{(entity.VideoCompletions?.toLocaleString() || entity.videoCompletions?.toLocaleString()) ?? 0}</div>
            <div>&nbsp;</div>
          </div>
        ),
      },
    );
  }

  return baseColumnOptions;
};

export const VideoPerformancePeopleComponent: FC<VideoPerformancePeopleProps> = ({
  actions,
  videoId,
  peopleDetailsEntities,
  peopleDetailsEntitiesStatus,
  filter,
  flowId,
  includeAccounts,
  childAccountId,
  hasCreateAccountsAddOn,
  setHasExportData,
  accountId,
}) => {
  const reportEnabled = useFeatureFlag(FeatureFlags.VideoReport);

  const columnOptions = getColumnOptions(reportEnabled, includeAccounts, childAccountId, hasCreateAccountsAddOn);

  useEffect(() => {
    setHasExportData({
      hasDataToExport: peopleDetailsEntitiesStatus.status === RStatus.Got && !!peopleDetailsEntities.length,
      tooltipContent: "No data available for export. This video contains no data.",
    });
  }, [setHasExportData, peopleDetailsEntitiesStatus.status, peopleDetailsEntities]);

  // Fetch all sends
  useEffect(() => {
    if (reportEnabled && filter.type !== undefined) {
      actions.fetchEngagementPeopleData(videoId, { ...filter, flowId, allItems: false });
    }
  }, [videoId, actions, filter, flowId, reportEnabled, accountId]);

  return (
    <RequestStatusRenderer state={peopleDetailsEntitiesStatus}>
      <GenericPerformanceList rows={peopleDetailsEntities} columns={columnOptions} filter={filter} title="People" />
    </RequestStatusRenderer>
  );
};

const mapStateToProps = (state: RootState) => {
  return {
    accountId: state.userProfile.accountId,
    peopleDetailsEntities: selectEngagementPeopleValue(state),
    peopleDetailsEntitiesStatus: selectEngagementPeopleData(state),
  };
};

const mapDispatchToProps = (dispatch: AppDispatch) => {
  return {
    actions: bindActionCreators(videoPerformanceActions, dispatch),
    setHasExportData: bindAction(setHasDataToExport, dispatch),
  };
};

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

const ConnectedComponent = connector(VideoPerformancePeopleComponent);
export default ConnectedComponent;
